import React from 'react';
import { Box, IconButton, type SxProps, type Theme } from '@mui/material';
import { X } from 'lucide-react';

export type RemovableTokenTone = 'neutral' | 'accent' | 'muted';

export interface RemovableTokenProps {
  /** Short qualifier shown before the value (e.g. "Beds"). */
  prefix?: React.ReactNode;
  /** The token's main text (e.g. "3+"). */
  label: React.ReactNode;
  /** Leading icon node. */
  icon?: React.ReactNode;
  /** Omitting this renders a read-only token with no remove affordance. */
  onRemove?: () => void;
  /** Accessible label for the remove button. Defaults to "Remove <label>". */
  removeLabel?: string;
  tone?: RemovableTokenTone;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  testId?: string;
}

const TONE_STYLES: Record<RemovableTokenTone, { bg: string; color: string }> = {
  neutral: { bg: '#fff', color: '#1a1a1a' },
  accent: { bg: '#eef4ff', color: '#12408a' },
  muted: { bg: '#f4f4f5', color: '#575757' },
};

/**
 * RemovableToken - Compact chip representing one applied value, with a remove control.
 *
 * Atomic Level: Atom
 *
 * Built for surfaces that turn parsed input into individually dismissible
 * pieces (e.g. natural-language search terms mapped to filters).
 *
 * @example
 * ```tsx
 * <RemovableToken prefix="Beds" label="3+" onRemove={() => clear('bedrooms')} />
 * ```
 */
export function RemovableToken({
  prefix,
  label,
  icon,
  onRemove,
  removeLabel,
  tone = 'neutral',
  disabled = false,
  sx,
  testId,
}: RemovableTokenProps) {
  const { bg, color } = TONE_STYLES[tone];
  const fallbackRemoveLabel = typeof label === 'string' ? `Remove ${label}` : 'Remove';

  return (
    <Box
      component="span"
      data-testid={testId}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        pl: '10px',
        pr: onRemove ? '4px' : '10px',
        py: '4px',
        borderRadius: '100px',
        backgroundColor: bg,
        color,
        fontSize: '12px',
        lineHeight: 1.3,
        maxWidth: '100%',
        opacity: disabled ? 0.5 : 1,
        ...sx,
      }}
    >
      {icon && (
        <Box component="span" aria-hidden sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {icon}
        </Box>
      )}
      {prefix && (
        <Box component="span" sx={{ color: 'inherit', opacity: 0.65 }}>
          {prefix}
        </Box>
      )}
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
      {onRemove && (
        <IconButton
          type="button"
          size="small"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeLabel || fallbackRemoveLabel}
          sx={{
            p: '2px',
            color: 'inherit',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
          }}
        >
          <X size={14} strokeWidth={2.5} aria-hidden />
        </IconButton>
      )}
    </Box>
  );
}

export default RemovableToken;
